
import React, { ReactNode } from 'react';
import { LayoutDashboard, Users, Settings, LogOut, Building2, MessageSquare, ChevronDown, CheckCircle2, UserCircle, Globe } from 'lucide-react';
import { View } from '../types';
import Logo from './Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, Language } from '../i18n/translations';

interface LayoutProps {
  children: ReactNode;
  currentView: View;
  setCurrentView: (view: View) => void;
  onLogout?: () => void;
  userName?: string;
  role?: 'business' | 'individual' | 'owner';
}

const Layout: React.FC<LayoutProps> = ({ children, currentView, setCurrentView, onLogout, userName, role }) => {
  const { t, language, setLanguage } = useLanguage();

  const handleLanguageChange = (code: Language) => {
    setLanguage(code);
  };

  const navItems = [
    { id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { id: 'inquiries', label: t('nav.inquiries'), icon: MessageSquare },
    { id: 'individuals', label: t('nav.bookings'), icon: Users },
    { id: 'businesses', label: t('nav.vendors'), icon: Building2 },
  ];

  if (role === 'business') {
    navItems.push({ id: 'profile', label: 'Profile', icon: UserCircle });
  }

  const handleSafeLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onLogout) onLogout();
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar - Desktop Only */}
      <aside className="w-72 bg-white shadow-[10px_0_30px_rgba(0,0,0,0.02)] flex flex-col z-50 hidden md:flex border-r border-gray-100">
        <div className="p-8 border-b border-gray-50 flex items-center justify-center group">
          <div className="mr-3 text-wedding-600 transform group-hover:rotate-12 transition-transform">
             <Logo className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-serif font-black tracking-tighter text-gray-900 truncate">Event Manager</h1>
        </div>

        <nav className="flex-1 p-6 space-y-3">
          {navItems.map((item) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.id as View)}
                className={`w-full flex items-center px-5 py-4 rounded-2xl transition-all duration-300 ${
                  isActive
                    ? 'bg-wedding-600 text-white font-bold shadow-xl shadow-wedding-200'
                    : 'text-gray-400 hover:text-wedding-600 hover:bg-wedding-50'
                }`}
              >
                <item.icon 
                   className={`w-5 h-5 transition-transform ${isActive ? 'scale-110 mr-4' : 'mr-4'}`} 
                />
                <span className="text-sm font-black uppercase tracking-widest">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-6 border-t border-gray-50 space-y-4">
          <div className="relative group">
            <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-2xl transition-all border border-gray-100 hover:bg-white hover:shadow-md">
               <span className="flex items-center">
                 <span className="text-xl mr-3">{SUPPORTED_LANGUAGES.find(l => l.code === language)?.flag}</span>
                 <span className="text-xs font-black uppercase tracking-widest text-gray-500">{SUPPORTED_LANGUAGES.find(l => l.code === language)?.name}</span>
               </span>
               <ChevronDown className="w-4 h-4 text-gray-300" />
            </button>
            <div className="absolute bottom-full left-0 mb-4 w-full max-h-64 overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[100]">
                <div className="p-2">
                    {SUPPORTED_LANGUAGES.map(lang => (
                    <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full text-left px-4 py-3 text-xs font-bold rounded-xl hover:bg-gray-50 flex items-center justify-between ${language === lang.code ? 'bg-wedding-50 text-wedding-700' : 'text-gray-600'}`}
                    >
                        <span className="flex items-center gap-3">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                        </span>
                        {language === lang.code && <CheckCircle2 className="w-3 h-3" />}
                    </button>
                    ))}
                </div>
            </div>
          </div>

          <button 
            onClick={handleSafeLogout}
            className="w-full flex items-center p-4 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all font-black uppercase tracking-widest text-xs"
          >
            <LogOut className="w-5 h-5 mr-4" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col bg-gray-50/50 relative pb-20 md:pb-0">
        {/* Header - Fixed on mobile, scroll with content on desktop */}
        <header className="bg-white shadow-[0_10px_30px_rgba(0,0,0,0.02)] px-4 py-4 md:px-10 md:py-6 sticky top-0 z-40 block border-b border-gray-50">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3 md:gap-4">
                <div className="w-1.5 md:w-2 h-6 md:h-8 bg-wedding-600 rounded-full"></div>
                <h2 className="text-lg md:text-3xl font-serif font-black text-gray-900 tracking-tight">
                    {navItems.find(i => i.id === currentView)?.label || 'Overview'}
                </h2>
            </div>
            <div className="flex items-center space-x-4 md:space-x-8">
              <div className="text-right hidden sm:block">
                <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('dashboard.welcome')}</p>
                <p className="text-xs md:text-sm font-black text-gray-900">{userName || 'User'}</p>
              </div>
              
              <div className="relative group">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-wedding-100 to-wedding-200 flex items-center justify-center text-wedding-700 font-black shadow-lg border-2 border-white cursor-pointer hover:rotate-12 transition-transform text-sm md:text-base">
                  {userName ? userName.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="absolute right-0 mt-4 w-48 md:w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 z-50">
                    <div className="px-5 py-2 mb-2 border-b border-gray-50">
                        <p className="text-[9px] md:text-[10px] font-black text-gray-300 uppercase tracking-widest">Account Hub</p>
                    </div>
                    <button 
                        onClick={handleSafeLogout}
                        className="w-full text-left px-5 py-3 text-[10px] md:text-xs font-black uppercase tracking-widest text-red-500 hover:bg-red-50 flex items-center"
                    >
                        <LogOut className="w-4 h-4 mr-3" />
                        Terminate Session
                    </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 md:p-10 max-w-7xl mx-auto w-full flex-1">
          {children}
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-100 z-50 flex justify-around items-center py-3 px-2 shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
          {navItems.slice(0, 5).map((item) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.id as View)}
                className={`flex flex-col items-center gap-1.5 transition-colors ${
                  isActive ? 'text-wedding-600' : 'text-gray-400'
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'scale-110' : ''}`} />
                <span className="text-[8px] font-black uppercase tracking-tighter">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </main>
    </div>
  );
};

export default Layout;
